import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Patient } from "../patient.model";

interface NeurologicalFields {
  /**
   * The patient's extremity movement.
   */
  extremityMovement: string;

  /**
   * The strength of the patient's hand grips.
   *
   * Options: strong, moderate, or weak.
   */
  handGrips: string;

  /**
   * The strength of the patient's lower extremity push/pull.
   *
   * Options: strong, moderate, or weak.
   */
  lePushPull: string;

  /**
   * The patient's Glasgow Coma Scale score.
   */
  gcss: string;

  // Sensation

  /**
   * Sensation in right upper extremity.
   *
   * Options: numbness or tingling.
   */
  sensationRue: string;

  /**
   * Sensation in right lower extremity.
   *
   * Options: numbness or tingling.
   */
  sensationRle: string;

  /**
   * Sensation in left lower extremity.
   *
   * Options: numbness or tingling.
   */
  sensationLle: string;

  /**
   * Sensation in left upper extremity.
   *
   * Options: numbness or tingling.
   */
  sensationLue: string;

  /**
   * Extremities where the patient experiences tremors.
   *
   * Options: RUE, RLE, LUE, and LLE.
   */
  tremors: string[];

  isEpidural: boolean;
  sensoryBlockLevel: string;
  abnormalReflexesNoted: string;

  /**
   * For Pediatrics:
   * 
   * Abnormal reflexes type.
   * Options: Jittery, Absent, Diminished, Other
   */
  abnormalReflexesType: '' | 'jittery' | 'absent' | 'diminished' | 'other';

  /**
   * Additional details for abnormal reflexes.
   */
  abnormalReflexesDetails: string;

  /**
   * Whether abnormal reflexes are present (true) or not (false).
   */
  abnormalReflexesPresent: boolean;

  otherFindings: string;
}

export type NeurologicalFormGroup = FormGroup<ControlsOf<NeurologicalFields>>;

export const NeurologicalAssessmentHelpers: AssessmentHelpers<NeurologicalAssessment, Patient> = {
  createFormGroup: createNeurologicalFormGroup,
  toPDF: neurologicalAssessmentToPDF,
  formToObject: formToNeurologicalAssessment,
  initialState: {
    extremityMovement: '',
    handGrips: '',
    lePushPull: '',
    gcss: '',
    sensationRue: '',
    sensationRle: '',
    sensationLle: '',
    sensationLue: '',
    tremors: [],

    // OB only
    isEpidural: false,
    sensoryBlockLevel: '',
    abnormalReflexesNoted: '',

    // Pediatric only
    abnormalReflexesType: '',
    abnormalReflexesDetails: '',
    abnormalReflexesPresent: false,
    otherFindings: '',
  },
};

/**
 * Holds information about the patient's neurological assessment.
 */
export class NeurologicalAssessment {
  extremityMovement: string;
  handGrips: string;
  lePushPull: string;
  gcss: string;
  sensationRue: string;
  sensationRle: string;
  sensationLle: string;
  sensationLue: string;
  tremors: string[];

  // OB only
  isEpidural: boolean;
  sensoryBlockLevel: string;
  abnormalReflexesNoted: string;

  // Pediatric only
  abnormalReflexesType: '' | 'jittery' | 'absent' | 'diminished' | 'other';
  abnormalReflexesDetails: string;
  abnormalReflexesPresent: boolean;
  otherFindings: string;

  constructor(data: Partial<NeurologicalFields>) {
    const is = NeurologicalAssessmentHelpers.initialState;
    this.extremityMovement = data.extremityMovement || is['extremityMovement'];
    this.handGrips = data.handGrips || is['handGrips'];
    this.lePushPull = data.lePushPull || is['lePushPull'];
    this.gcss = data.gcss || is['gcss'];
    this.sensationRue = data.sensationRue || is['sensationRue'];
    this.sensationRle = data.sensationRle || is['sensationRle'];
    this.sensationLle = data.sensationLle || is['sensationLle'];
    this.sensationLue = data.sensationLue || is['sensationLue'];
    this.tremors = data.tremors || is['tremors'];
    this.isEpidural = data.isEpidural ?? is['isEpidural'];
    this.sensoryBlockLevel = data.sensoryBlockLevel || is['sensoryBlockLevel'];
    this.abnormalReflexesNoted = data.abnormalReflexesNoted || is['abnormalReflexesNoted'];
    this.abnormalReflexesType = data.abnormalReflexesType ?? is['abnormalReflexesType'];
    this.abnormalReflexesDetails = data.abnormalReflexesDetails ?? is['abnormalReflexesDetails'];
    this.abnormalReflexesPresent = data.abnormalReflexesPresent ?? is['abnormalReflexesPresent'];
    this.otherFindings = data.otherFindings || is['otherFindings'];
  }
}

function createNeurologicalFormGroup(neurologicalAssessment?: NeurologicalAssessment) {
  const data = neurologicalAssessment || new NeurologicalAssessment(NeurologicalAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<NeurologicalFields>>({
    extremityMovement: new FormControl<string | null>(data.extremityMovement),
    handGrips: new FormControl<string | null>(data.handGrips),
    lePushPull: new FormControl<string | null>(data.lePushPull),
    gcss: new FormControl<string | null>(data.gcss),
    sensationRue: new FormControl<string | null>(data.sensationRue),
    sensationRle: new FormControl<string | null>(data.sensationRle),
    sensationLle: new FormControl<string | null>(data.sensationLle),
    sensationLue: new FormControl<string | null>(data.sensationLue),
    tremors: new FormControl<string[] | null>(data.tremors),

    // OB only
    isEpidural: new FormControl<boolean | null>(data.isEpidural),
    sensoryBlockLevel: new FormControl<string | null>(data.sensoryBlockLevel),
    abnormalReflexesNoted: new FormControl<string | null>(data.abnormalReflexesNoted),

    // Pediatric only
    abnormalReflexesType: new FormControl<'' | 'jittery' | 'absent' | 'diminished' | 'other' | null>(data.abnormalReflexesType),
    abnormalReflexesDetails: new FormControl<string | null>(data.abnormalReflexesDetails),
    abnormalReflexesPresent: new FormControl<boolean | null>(data.abnormalReflexesPresent),
    otherFindings: new FormControl<string | null>(data.otherFindings),
  });
}

function neurologicalAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: NeurologicalAssessment,
  patient: Patient): void {
  pdf.addSectionTitle("Neurological");
  pdf.addOneColumnText("Extremity Movement", assessment.extremityMovement);
  pdf.addTwoColumnText("Hand Grips", assessment.handGrips, "LE Push/Pull", assessment.lePushPull);
  pdf.addOneColumnText("Glascow Coma Scale Score", assessment.gcss);
  pdf.currY += 10;
  pdf.pdf.text("Sensation", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("RUE", assessment.sensationRue, "LUE", assessment.sensationLue);
  pdf.addTwoColumnText("RLE", assessment.sensationRle, "LLE", assessment.sensationLle);
  pdf.addOneColumnText("Tremors", assessment.tremors.join(", "));
  if (patient.isObstetric()) {
    pdf.addTwoColumnText("Level of Sensory Block", assessment.sensoryBlockLevel,
      "Abnormal Reflexes Noted", assessment.abnormalReflexesNoted);
  } else {
    pdf.addTwoColumnText("Level of Sensory Block", assessment.sensoryBlockLevel, "", "");
  }
  if (patient.isPediatric()) {
    pdf.addTwoColumnText("Abnormal Reflexes", assessment.abnormalReflexesPresent ? "Yes" : "No", "", "");
    if (assessment.abnormalReflexesPresent) {
      pdf.addTwoColumnText("Abnormal Reflex Type", assessment.abnormalReflexesType,
        "Details", assessment.abnormalReflexesDetails);
    }
  }
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);
}

function formToNeurologicalAssessment(fg: NeurologicalFormGroup) {
  return new NeurologicalAssessment({
    extremityMovement: fg.controls.extremityMovement.value!,
    handGrips: fg.controls.handGrips.value!,
    lePushPull: fg.controls.lePushPull.value!,
    gcss: fg.controls.gcss.value!,
    sensationRue: fg.controls.sensationRue.value!,
    sensationRle: fg.controls.sensationRle.value!,
    sensationLle: fg.controls.sensationLle.value!,
    sensationLue: fg.controls.sensationLue.value!,
    tremors: fg.controls.tremors.value!,
    isEpidural: fg.controls.isEpidural.value!,
    sensoryBlockLevel: fg.controls.sensoryBlockLevel.value!,
    abnormalReflexesNoted: fg.controls.abnormalReflexesNoted.value!,
    abnormalReflexesType: fg.controls.abnormalReflexesType.value!,
    abnormalReflexesDetails: fg.controls.abnormalReflexesDetails.value!,
    abnormalReflexesPresent: fg.controls.abnormalReflexesPresent.value!,
    otherFindings: fg.controls.otherFindings.value!,
  });
}